import {
  DetailsList,
  DetailsListLayoutMode,
  IColumn,
  Icon,
  SelectionMode,
  mergeStyleSets,
  Link,
  Callout,
  DirectionalHint,
  IconButton,
  Dialog,
  DialogType,
  DialogFooter,
  DefaultButton,
} from "@fluentui/react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";
import styled from "styled-components";
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
import "@cyntler/react-doc-viewer/dist/index.css";
import { useSelector } from "../../store/hooks";
import { IStyledTheme } from "../../theme/types";
import { formatDateString } from "../../utils/date";

const DocViewerContainer = styled.div`
  width: 100%;
  height: 80vh;
  min-height: 600px;
  overflow: auto;

  /* Ensure DocViewer and its children fill the container */
  #react-doc-viewer {
    height: 100% !important;
  }

  #proxy-renderer {
    height: 100% !important;
  }

  #msdoc-renderer,
  #image-renderer,
  #pdf-renderer {
    height: 100% !important;
  }

  iframe {
    height: 100% !important;
    min-height: 550px;
  }
`;

function ReportHistoryTable() {
  const { t } = useTranslation();

  const reportHistory = useSelector((s) => s.reportPage.reportHistory);

  const theme = useTheme() as IStyledTheme;

  const classes = getClassNames(theme);

  const [attachmentCallout, setAttachmentCallout] = useState<{
    isVisible: boolean;
    targetId: string | null;
    attachments: any[];
  }>({ isVisible: false, targetId: null, attachments: [] });

  const [previewDialog, setPreviewDialog] = useState<{
    isOpen: boolean;
    documents: { uri: string; fileName?: string }[];
  }>({ isOpen: false, documents: [] });

  const openPreview = (attachments: any[]) => {
    const docs = attachments.map((att: any) => ({
      uri: att.attachmentPath,
      fileName: att.attachmentName,
    }));
    setPreviewDialog({ isOpen: true, documents: docs });
  };

  const closePreview = () => {
    setPreviewDialog({ isOpen: false, documents: [] });
  };

  // Transform reportHistory to flat structure for table display
  const items =
    reportHistory?.map((item) => ({
      ...item.report,
      reportAttachments: item.reportAttachments,
    })) || [];

  const columns: IColumn[] = [
    {
      key: "surveyDate",
      name: t("ram.reportForm.surveyDate"),
      fieldName: "surveyDate",
      minWidth: 100,
      maxWidth: 100,
      isResizable: true,
      isCollapsible: true,

      onRender: (row) => {
        if (!row?.surveyDate) return null;

        return <span>{formatDateString(row?.surveyDate)}</span>;
      },
    },
    {
      key: "reportName",
      name: t("ram.reportForm.reportName"),
      fieldName: "reportName",
      minWidth: 200,
      isResizable: true,
      isCollapsible: true,
      onRender: (row) => {
        if (!row?.reportName) return null;

        return <span>{row.reportName}</span>;
      },
    },
    {
      key: "emlinMioEur",
      name: t("ram.reportForm.EMLInMioEUR.label"),
      fieldName: "emlinMioEur",
      minWidth: 120,
      maxWidth: 150,
      isResizable: true,
      isCollapsible: true,
      onRender: (row) => {
        if (row?.emlinMioEur == null) return null;

        return <span>{row.emlinMioEur}</span>;
      },
    },
    {
      key: "pmlinMioEur",
      name: t("ram.reportForm.pmlinMioEur.label"),
      fieldName: "pmlinMioEur",
      minWidth: 120,
      maxWidth: 150,
      isResizable: true,
      isCollapsible: true,
      onRender: (row) => {
        if (row?.pmlinMioEur == null) return null;

        return <span>{row.pmlinMioEur}</span>;
      },
    },
    {
      key: "insurersRaiting",
      name: t("ram.reportForm.insurersRaiting.label"),
      fieldName: "insurersRaiting",
      minWidth: 120,
      maxWidth: 150,
      isResizable: true,
      isCollapsible: true,
      onRender: (row) => {
        if (!row?.insurersRaiting) return null;

        return <span>{row.insurersRaiting}</span>;
      },
    },

    {
      key: "attachments-key",
      name: t("ram.reportForm.attachments.label"),
      fieldName: "reportAttachments",
      minWidth: 150,
      maxWidth: 200,
      isResizable: true,
      isCollapsible: true,
      onRender: (row) => {
        if (!row?.reportAttachments || row.reportAttachments.length === 0)
          return null;

        const targetId = `attachments-${row.reportId}`;

        return (
          <>
            <div
              id={targetId}
              style={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
              }}
              onClick={() =>
                setAttachmentCallout({
                  isVisible: true,
                  targetId: `#${targetId}`,
                  attachments: row.reportAttachments,
                })
              }
            >
              <Icon
                iconName="Attach"
                styles={{
                  root: {
                    fontSize: "16px",
                  },
                }}
              />
              <span style={{ marginLeft: "4px", fontSize: "12px" }}>
                ({row.reportAttachments.length})
              </span>
            </div>
          </>
        );
      },
    },
  ];

  return (
    <div>
      {items?.length > 0 ? (
        <>
          <div className={classes.label}>
            <h5> {t("ram.reportForm.reportsHistory")}</h5>
          </div>
          <DetailsList
            items={items}
            compact={false}
            columns={columns || []}
            selectionMode={SelectionMode.none}
            setKey="single"
            isSelectedOnFocus
            layoutMode={DetailsListLayoutMode.justified}
            isHeaderVisible={true}
            selectionPreservedOnEmptyClick={true}
            enterModalSelectionOnTouch={true}
            ariaLabelForSelectionColumn="Toggle selection"
            ariaLabelForSelectAllCheckbox="Toggle selection for all items"
            checkButtonAriaLabel="Row checkbox"
            className={classes.table}
          />
        </>
      ) : null}
      {attachmentCallout.isVisible && attachmentCallout.targetId && (
        <Callout
          target={attachmentCallout.targetId}
          onDismiss={() =>
            setAttachmentCallout({
              isVisible: false,
              targetId: null,
              attachments: [],
            })
          }
          directionalHint={DirectionalHint.bottomLeftEdge}
          gapSpace={4}
          setInitialFocus
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              padding: "12px",
              minWidth: "200px",
            }}
          >
            {attachmentCallout.attachments.length > 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginBottom: "4px",
                }}
              >
                <IconButton
                  iconProps={{ iconName: "View" }}
                  title={t("ram.responsesPage.attachments.previewDocuments")}
                  onClick={() => openPreview(attachmentCallout.attachments)}
                  styles={{
                    root: { height: 24, width: 24 },
                    icon: { fontSize: 14 },
                  }}
                />
              </div>
            )}
            {attachmentCallout.attachments.map(
              (attachment: any, index: number) => (
                <Link
                  key={index}
                  href={attachment.attachmentPath}
                  target="_blank"
                  style={{ fontSize: "12px" }}
                >
                  {attachment.attachmentName}
                </Link>
              ),
            )}
          </div>
        </Callout>
      )}
      <Dialog
        hidden={!previewDialog.isOpen}
        onDismiss={closePreview}
        dialogContentProps={{
          type: DialogType.normal,
          title: t("ram.responsesPage.attachments.previewDocuments"),
          closeButtonAriaLabel: "Close",
        }}
        minWidth={800}
        maxWidth={1200}
      >
        <DocViewerContainer>
          <DocViewer
            documents={previewDialog.documents}
            pluginRenderers={DocViewerRenderers}
            config={{
              header: {
                disableHeader: false,
                disableFileName: false,
              },
            }}
          />
        </DocViewerContainer>
        <DialogFooter>
          <DefaultButton
            onClick={closePreview}
            text={t("ram.reportDocumentDialogDownloadClose.label")}
          />
        </DialogFooter>
      </Dialog>
    </div>
  );
}

export default ReportHistoryTable;

const getClassNames = (theme: IStyledTheme) =>
  mergeStyleSets({
    label: {
      paddingBottom: 10,
      color: theme.palette.black,
    },
    table: {
      ".ms-List-cell:nth-child(odd) .ms-DetailsRow": {
        background: theme.isDark
          ? "rgb(2, 7, 10)"
          : theme.palette?.themeLighterAlt,
      },
      ".ms-DetailsRow ": {
        height: "30px !important",
      },

      ".ms-List-cell:hover, .ms-List-cell .is-selected": {
        background: theme.isDark
          ? "rgb(9, 27, 41) !important"
          : "rgb(208, 231, 248) !important",
      },
      ".ms-List-cell .ms-DetailsRow:hover": {
        background: theme.isDark
          ? "rgb(9, 27, 41) !important"
          : "rgb(208, 231, 248) !important",
      },

      ".ms-DetailsHeader-cellTitle": {
        justifyContent: "flex-start",
      },
      ".ms-DetailsHeader-cell:hover": {
        background: "transparent" /* Override hover background color */,
      },

      "[data-item-key='download-key'] *": {
        display: "flex",
        justifyContent: "center",
      },
      ".ms-DetailsHeader-cellName i": {
        fontSize: 16,
        marginRight: "5px",
        fontWeight: "400",
      },
      ".ms-DetailsHeader": {
        display: "flex",
        justifyContent: "space-between",
        background: theme.palette.neutralLight,
        paddingTop: 0,
      },

      ".ms-DetailsRow-cell": {
        display: "flex",
        justifyContent: "flex-start",
      },
      ".ms-DetailsRow-cell:first-child": {
        display: "flex",
        justifyContent: "flex-start",
      },
      ".ms-DetailsRow-cell:last-child": {
        display: "flex",
        justifyContent: "center",
      },
    },
  });
